import { useCallback, useEffect, useState } from 'react';
import { usePagedList } from '../../hooks/usePagedList';
import { ListCount, LoadMore } from './AdminPaging';
import { useLoadingState } from '../../hooks';
import { LoadingPanel } from '../brand/TwindenLoader';
import {
  Badge,
  Box,
  Button,
  ButtonGroup,
  HStack,
  Link,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import type { ReportReason } from '@twinden/shared';
import {
  adminApi,
  ApiError,
  type AdminReport,
  type ReportPerson,
} from '../../lib/api';
import { appLocale } from '../../lib/format';

// Every report, for the system root (M58, docs/report-block-contact.md
// D210): unhandled first, then newest. Ids, reason and note only; the server
// can't read what was said.

const REASON: Record<ReportReason, string> = {
  harassment: 'Harassment or abuse',
  spam: 'Spam or scam',
  impersonation: 'Pretending to be someone',
  other: 'Something else',
};

type Filter = 'unhandled' | 'handled' | 'all';

function Person({
  person,
  onOpen,
}: {
  person: ReportPerson | null;
  onOpen: (id: string) => void;
}) {
  if (!person) return <Text as="span">Deleted account</Text>;
  const label = person.deleted
    ? 'Deleted account'
    : `${person.displayName}${person.handle ? ` @${person.handle}` : ''}`;
  return (
    <Link
      as="button"
      type="button"
      color="accent"
      fontWeight={700}
      onClick={() => onOpen(person.id)}
    >
      {label}
    </Link>
  );
}

export function ReportsTab({
  onCount,
  onOpenFamily,
  onOpenUser,
}: {
  /** Unhandled reports, for the tab's badge. */
  onCount?: (count: number) => void;
  onOpenFamily: (familyId: string) => void;
  onOpenUser: (userId: string) => void;
}) {
  const toast = useToast();
  const [filter, setFilter] = useState<Filter>('unhandled');
  const [busy, setBusy] = useState<string | null>(null);

  // 50 a page, unhandled first (#73); a new filter starts again.
  const fetchPage = useCallback(
    async (cursor: string | null) =>
      (await adminApi.getReports(filter, { cursor })).data,
    [filter]
  );
  const list = usePagedList<AdminReport, Awaited<ReturnType<typeof fetchPage>>>(
    fetchPage
  );
  const reports = list.items;
  const unhandledCount = list.extra?.unhandledCount;
  useEffect(() => {
    if (unhandledCount !== undefined) onCount?.(unhandledCount);
  }, [unhandledCount, onCount]);

  const mark = async (report: AdminReport, handled: boolean) => {
    setBusy(report.id);
    try {
      await adminApi.markReport(report.id, handled);
      await list.reload();
    } catch (err) {
      toast({
        title: 'Could not save',
        description:
          err instanceof ApiError ? err.message : 'Something went wrong',
        status: 'error',
      });
    } finally {
      setBusy(null);
    }
  };

  const reportsState = useLoadingState({
    loading: reports === null && !list.error,
    error: list.error,
    retry: list.retry,
  });

  return (
    <VStack align="stretch" spacing={4}>
      <Text color="muted" fontSize="sm">
        Members reported by someone in their family. Reports about a root are
        seen only here.
      </Text>
      <ButtonGroup size="sm" isAttached variant="outline" colorScheme="gray">
        {(['unhandled', 'handled', 'all'] as const).map((value) => (
          <Button
            key={value}
            borderColor="line"
            isActive={filter === value}
            onClick={() => setFilter(value)}
          >
            {value === 'unhandled'
              ? 'Unhandled'
              : value === 'handled'
                ? 'Handled'
                : 'All'}
          </Button>
        ))}
      </ButtonGroup>
      <ListCount list={list} one="report" many="reports" />
      {reports === null || reportsState.show ? (
        <LoadingPanel state={reportsState} />
      ) : reports.length === 0 ? (
        <Text color="muted">No reports.</Text>
      ) : (
        reports.map((report) => (
          <Box
            key={report.id}
            bg="surface"
            borderWidth="1px"
            borderColor="line"
            borderRadius="card"
            p={4}
          >
            <VStack align="stretch" spacing={2}>
              <HStack
                justify="space-between"
                align="flex-start"
                flexWrap="wrap"
              >
                <Text fontWeight={800} fontSize="17px">
                  {REASON[report.reason]}
                </Text>
                <HStack>
                  {!report.shownToFamily && (
                    <Badge colorScheme="purple" borderRadius="full" px={2}>
                      About a root
                    </Badge>
                  )}
                  <Badge
                    colorScheme={report.handledAt ? 'green' : 'orange'}
                    borderRadius="full"
                    px={2}
                    textTransform="none"
                  >
                    {report.handledAt ? 'Handled' : 'New'}
                  </Badge>
                </HStack>
              </HStack>
              <Text fontSize="15px">
                <Person person={report.reporter} onOpen={onOpenUser} /> reported{' '}
                <Person person={report.reported} onOpen={onOpenUser} /> in{' '}
                <Link
                  as="button"
                  type="button"
                  color="accent"
                  fontWeight={700}
                  onClick={() => onOpenFamily(report.family.id)}
                >
                  {report.family.name}
                </Link>
              </Text>
              {report.note && (
                <Text whiteSpace="pre-wrap" color="ink.body">
                  {report.note}
                </Text>
              )}
              <Text fontSize="xs" color="muted" wordBreak="break-all">
                {new Date(report.createdAt).toLocaleString(appLocale())}
                {report.conversationId
                  ? ` · chat ${report.conversationId}`
                  : ''}
                {report.messageId ? ` · message ${report.messageId}` : ''}
                {report.handledAt
                  ? ` · handled ${new Date(report.handledAt).toLocaleDateString(appLocale())}${report.handledBySystemRoot ? ' by you' : ' by a root'}`
                  : ''}
              </Text>
              <Box>
                <Button
                  size="sm"
                  variant={report.handledAt ? 'outline' : 'solid'}
                  colorScheme={report.handledAt ? 'gray' : undefined}
                  isLoading={busy === report.id}
                  onClick={() => void mark(report, !report.handledAt)}
                >
                  {report.handledAt ? 'Mark unhandled' : 'Mark handled'}
                </Button>
              </Box>
            </VStack>
          </Box>
        ))
      )}
      <LoadMore list={list} />
    </VStack>
  );
}
